/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/deviceVersion.ts
 * Author: SWAN
 * Created: 2026-02-23
 * Description: 기기 펌웨어 버전 파싱 및 비교 유틸리티
 */

/**
 * 펌웨어 버전 문자열을 숫자로 변환
 * @param versionString - "v5.5.0" 또는 "5.5.0" 형식의 버전 문자열
 * @returns 버전 숫자 (예: 50500) 또는 0 (파싱 실패 시)
 * @example
 * parseDeviceVersion("v5.5.0") // 50500
 * parseDeviceVersion("5.5.0")  // 50500
 * parseDeviceVersion("0.4.0")  // 400
 * parseDeviceVersion("0.4.1")  // 401
 */
function parseDeviceVersion(versionString: string | null | undefined): number {
  if (!versionString) {
    return 0;
  }

  // "v" 제거
  const cleanVersion = versionString.replace(/^v/i, '');

  // 숫자와 점만 추출
  const parts = cleanVersion.split('.').map(part => parseInt(part, 10));

  if (parts.length < 2 || parts.some(p => isNaN(p))) {
    console.warn(`[parseDeviceVersion] 잘못된 버전 형식: ${versionString}`);
    return 0;
  }

  // major.minor.patch 형식으로 변환 (예: 5.5.0 → 50500, 0.4.1 → 401)
  const major = parts[0] || 0;
  const minor = parts[1] || 0;
  const patch = parts[2] || 0;

  return major * 10000 + minor * 100 + patch;
}

/**
 * 기기가 압축된 gas reading 데이터를 전송하는지 확인
 * @param fwVersion - 펌웨어 버전 문자열
 * @returns 압축 데이터 전송 여부
 * @example
 * isCompressedGasReading("v0.4.1") // true
 * isCompressedGasReading("v0.4.0") // false
 * isCompressedGasReading("v0.3.9") // false
 */
export function isCompressedGasReading(fwVersion: string | null | undefined): boolean {
  const version = parseDeviceVersion(fwVersion);
  // 펌웨어 버전 0.4.1 이상부터 압축된 데이터 전송
  return version >= 401;
}
